/**
 * 🌐 API Configuration
 * Centralized configuration for all API endpoints
 * Supports development and production environments
 */

// Get base URL from environment variable or default based on environment
const getBaseURL = () => {
  // Check if we're using service binding for internal communication
  const useServiceBinding = import.meta.env.VITE_USE_SERVICE_BINDING === 'true';

  if (useServiceBinding && !import.meta.env.DEV) {
    // In production/dev with service binding, use relative URLs for internal communication
    return ''; // This will make all API calls go to same domain, then proxied via service binding
  }

  // Use environment variable (set in .env.development or .env.production)
  const envURL = import.meta.env.VITE_BACKEND_URL;

  if (envURL) {
    return envURL;
  }

  // Local development fallback (only when running vite dev locally)
  if (import.meta.env.DEV) {
    return 'http://localhost:8787';
  }

  // No fallback - VITE_BACKEND_URL must be set for builds
  throw new Error('VITE_BACKEND_URL environment variable is required for builds');
};

export const API_CONFIG = {
  // Base URLs
  baseURL: getBaseURL(),
  
  // Specific service URLs
  authURL: `${getBaseURL()}/api/auth`,
  apiURL: `${getBaseURL()}/api/v2`,
  
  // Specific endpoint builders
  endpoints: {
    // Authentication endpoints
    auth: {
      googleLogin: `${getBaseURL()}/api/auth/google/login`,
      session: `${getBaseURL()}/api/auth/session`,
      logout: `${getBaseURL()}/api/auth/logout`,
      oauthResult: `${getBaseURL()}/api/auth/oauth-result` // 🔒 One-time token exchange
    },
    
    // API v2 endpoints
    api: {
      session: `${getBaseURL()}/api/v2/session`,
      research: `${getBaseURL()}/api/v2/research`,
      validate: `${getBaseURL()}/api/v2/validate`,
      analysis: `${getBaseURL()}/api/v2/analysis`,
      prompts: `${getBaseURL()}/api/v2/prompts`,
      
      // Credits endpoints
      credits: {
        balance: `${getBaseURL()}/api/v2/credits/balance`,
        analytics: `${getBaseURL()}/api/v2/credits/analytics`,
        history: `${getBaseURL()}/api/v2/credits/history`
      },
      
      // User endpoints
      user: {
        planLimits: `${getBaseURL()}/api/v2/user/plan-limits`,
        profile: `${getBaseURL()}/api/v2/user/profile`
      },
      
      // Session management
      sessions: {
        history: `${getBaseURL()}/api/v2/session/history`,
        save: `${getBaseURL()}/api/v2/session/save`
      },

      // Deploy feature endpoints
      deploy: {
        connectionStatus: `${getBaseURL()}/api/v2/deploy/connection-status`,
        github: {
          oauth: `${getBaseURL()}/api/v2/deploy/github/oauth`,
          disconnect: `${getBaseURL()}/api/v2/deploy/github/disconnect`
        },
        cloudflare: {
          verify: `${getBaseURL()}/api/v2/deploy/cloudflare/verify`,
          disconnect: `${getBaseURL()}/api/v2/deploy/cloudflare/disconnect`
        }
      }
    }
  }
};

// Environment info for debugging
export const ENV_INFO = {
  isDevelopment: false,
  isProduction: true,
  backendURL: import.meta.env.VITE_BACKEND_URL || 'not set',
  resolvedBaseURL: getBaseURL()
};

// Helper function to get endpoint URLs
export const getEndpoint = (category, subcategory, endpoint) => {
  try {
    if (subcategory && endpoint) {
      return API_CONFIG.endpoints[category][subcategory][endpoint];
    } else if (subcategory) {
      return API_CONFIG.endpoints[category][subcategory];
    } else {
      return API_CONFIG.endpoints[category];
    }
  } catch (error) {
    return null;
  }
};

// Development logging removed for production